import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Modal } from "./Modal";
import { api, ApiError } from "../lib/api";
import { SearchableSelect } from "./SearchableSelect";

export type FieldKind =
    | "text" | "textarea" | "email" | "tel" | "number"
    | "date" | "datetime" | "time"
    | "select" | "searchselect" | "multiselect" | "checkbox";

export interface Option {
    value: string;
    label: string;
    /** Owning record for cascading selects, e.g. a contact's company id. */
    parent?: string | null;
}

/** A create form that opens INSIDE a select — "+ Add new…" without leaving. */
export interface AddNewConfig {
    title: string;
    subtitle?: string;
    /** POST target for the nested create, e.g. "/companies". */
    path: string;
    fields: Field[];
    /** Dropdown label for the created row; defaults to name/full name. */
    optionLabel?: (row: Record<string, unknown>) => string;
    /** Child form fields pre-filled (and hidden) from this form's values,
     *  e.g. { company_id: "company_id" } — a contact created from a lead is
     *  born linked to the lead's selected company. */
    inherit?: Record<string, string>;
    /** Initial values for the nested create — e.g. a website submission's
     *  organisation details prefilling the New organisation form. Visible and
     *  editable, unlike inherit. */
    initial?: Record<string, unknown>;
}

export interface Field {
    name: string;
    label: string;
    kind: FieldKind;
    required?: boolean;
    options?: Option[];
    help?: string;
    placeholder?: string;
    /** Half-width in the two-column grid. Defaults to full width. */
    half?: boolean;
    min?: number;
    max?: number;
    /** Text fields: offered as a dropdown of suggestions, typing stays free. */
    suggestions?: string[];
    /** Select fields: appends "+ Add new…" which opens this nested form. */
    addNew?: AddNewConfig;
    /** Select fields: fetch options live from a managed-list resource (e.g.
     *  "/opportunity-types"), using each row's `name` as both value and label —
     *  for dropdowns whose parent record stores the text, not a foreign key.
     *  Appends an inline "+ Add …" that POSTs { name } to the same resource and
     *  selects it. A resource that is empty or unreachable is never a dead end. */
    optionsFrom?: { path: string; addLabel?: string };
    /** Select fields: only offer options whose `parent` matches this sibling
     *  field's value. No parent chosen = all options offered. */
    cascadeFrom?: string;
    /** A reference link under the field, e.g. ABN Lookup. */
    helpLink?: { label: string; href: string };
}

type Value = string | number | boolean | string[] | null;
export type FormValues = Record<string, Value>;

interface Props {
    title: string;
    subtitle?: string;
    fields: Field[];
    /**
     * Existing row when editing; omitted for a create.
     *
     * Typed as `object` rather than `Record<string, unknown>` because interfaces
     * in TypeScript have no implicit index signature, so every caller passing a
     * declared row type (Employee, Policy, …) would fail to assign. The narrowing
     * back to a keyed record happens once, below, instead of at every call site.
     */
    initial?: object | null;
    /** POST target for create, e.g. "/employees". */
    path: string;
    /** Extra values merged into the payload, e.g. a parent id. */
    fixed?: Record<string, unknown>;
    onClose: () => void;
    /** Receives the created/updated row, so parents can chain on the result. */
    onSaved: (saved?: unknown) => void;
    /** Rendered live under the fields with the current values — e.g. the
     *  delivered shifts an invoice run would pick up. */
    preview?: (values: FormValues) => React.ReactNode;
}

const emptyFor = (field: Field): Value => {
    if (field.kind === "checkbox") return false;
    if (field.kind === "multiselect") return [];
    // Optional selects start unset. Defaulting to the first list entry looked
    // helpful but silently attached records to whatever sorted first — a
    // referral born linked to the wrong organisation. Required selects keep
    // the first option so the form never submits an impossible empty.
    if (field.kind === "select") return field.required ? field.options?.[0]?.value ?? "" : "";
    return "";
};

/** Trims an ISO timestamp to what <input type="datetime-local"> accepts. */
const toLocalInput = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const initialValues = (fields: Field[], initial?: object | null): FormValues => {
    const values: FormValues = {};
    const source = (initial ?? undefined) as Record<string, unknown> | undefined;
    for (const field of fields) {
        const raw = source?.[field.name];
        if (raw === undefined || raw === null) {
            values[field.name] = emptyFor(field);
            continue;
        }
        if (field.kind === "checkbox") values[field.name] = Boolean(raw);
        else if (field.kind === "multiselect") values[field.name] = Array.isArray(raw) ? (raw as string[]) : [];
        else if (field.kind === "date") values[field.name] = String(raw).slice(0, 10);
        else if (field.kind === "datetime") values[field.name] = toLocalInput(String(raw));
        else values[field.name] = raw as Value;
    }
    return values;
};

/**
 * One form component for every entity in the app.
 *
 * Field definitions live next to the page that owns them, so a screen's shape is
 * readable in one place, but validation and submission are shared. The server
 * remains the authority: this only catches required fields, and everything else
 * — enum membership, foreign keys, the compliance rules — comes back as an API
 * error and is shown verbatim rather than being second-guessed here.
 */
export const EntityForm = ({
    title, subtitle, fields, initial, path, fixed, onClose, onSaved, preview,
}: Props) => {
    const row = (initial ?? undefined) as Record<string, unknown> | undefined;
    const isEdit = typeof row?.id === "string";
    const [values, setValues] = useState<FormValues>(() => initialValues(fields, initial));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    // Options born inside this form via "+ Add new…" — appended to the field's
    // dropdown and auto-selected the moment the nested create saves.
    const [extraOptions, setExtraOptions] = useState<Record<string, Option[]>>({});
    const [fetchedOptions, setFetchedOptions] = useState<Record<string, Option[]>>({});
    const [addingFor, setAddingFor] = useState<Field | null>(null);

    // Managed-list fields fetch their options once, when the form opens. A
    // failure leaves the list empty — the "+ Add …" path still works, so a
    // brand-new list is never a dead end.
    useEffect(() => {
        let cancelled = false;
        for (const field of fields) {
            if (!field.optionsFrom) continue;
            api.get<Array<{ name: string; is_active?: boolean }>>(field.optionsFrom.path)
                .then((rows) => {
                    if (cancelled) return;
                    const opts = rows
                        .filter((r) => r.is_active !== false)
                        .map((r) => ({ value: r.name, label: r.name }));
                    setFetchedOptions((prev) => ({ ...prev, [field.name]: opts }));
                })
                .catch(() => { /* empty list; the add-new path is still available */ });
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const optionsFor = (field: Field): Option[] => {
        const all = [
            ...(field.options ?? []),
            ...(fetchedOptions[field.name] ?? []),
            ...(extraOptions[field.name] ?? []),
        ];
        const scoped = !field.cascadeFrom
            ? all
            : (() => {
                  const parent = values[field.cascadeFrom!];
                  return parent ? all.filter((o) => o.parent === undefined || o.parent === parent) : all;
              })();
        // Keep a stored value that isn't in the managed list visible (a legacy
        // free-text value entered before the field became a list), so editing a
        // record never silently drops it.
        const cur = values[field.name];
        if (field.optionsFrom && !field.cascadeFrom && typeof cur === "string" && cur
            && !scoped.some((o) => o.value === cur)) {
            return [...scoped, { value: cur, label: cur }];
        }
        return scoped;
    };

    // Changing the parent of a cascading select must not leave a child value
    // that is no longer offered — Priya Nair cannot stay picked after the
    // company flips to one she does not belong to.
    useEffect(() => {
        for (const field of fields) {
            if (!field.cascadeFrom) continue;
            const value = values[field.name];
            if (value && !optionsFor(field).some((o) => o.value === value)) {
                setValues((v) => ({ ...v, [field.name]: "" }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const missing = useMemo(
        () => fields.filter((f) => {
            if (!f.required) return false;
            const v = values[f.name];
            if (Array.isArray(v)) return v.length === 0;
            return v === "" || v === null || v === undefined;
        }),
        [fields, values]
    );

    const set = (name: string, value: Value) => setValues((v) => ({ ...v, [name]: value }));

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        setTouched(true);
        if (missing.length > 0) {
            setError(`Please complete: ${missing.map((f) => f.label).join(", ")}`);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const payload: Record<string, unknown> = { ...fixed };
            for (const field of fields) {
                const value = values[field.name];

                // An untouched optional text field must be sent as null, not "".
                // Empty string fails uuid, date and email validation server-side
                // and would surface as a confusing "invalid" on a field the user
                // never filled in.
                if (value === "" ) {
                    payload[field.name] = field.required ? "" : null;
                } else if (field.kind === "number") {
                    payload[field.name] = Number(value);
                } else if (field.kind === "datetime" && typeof value === "string") {
                    payload[field.name] = new Date(value).toISOString();
                } else {
                    payload[field.name] = value;
                }
            }

            const saved = isEdit
                ? await api.put<unknown>(`${path}/${row!.id as string}`, payload)
                : await api.post<unknown>(path, payload);
            onSaved(saved);
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? err.message
                    : err instanceof Error
                      ? err.message
                      : "Could not save"
            );
        } finally {
            setSaving(false);
        }
    };

    const isMissing = (field: Field) => touched && missing.includes(field);

    return (
        <Modal
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            width={fields.length > 6 ? "wide" : "narrow"}
            footer={
                <>
                    <button className="btn" onClick={submit} disabled={saving}>
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
                    </button>
                    <button className="btn ghost" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                </>
            }
        >
            <form onSubmit={submit} noValidate>
                {error && <div className="error-banner">{error}</div>}

                <div className="form-grid">
                    {fields.map((field) => {
                        const id = `f-${field.name}`;
                        const value = values[field.name];
                        const invalid = isMissing(field);

                        return (
                            <div
                                key={field.name}
                                className={`field ${field.half ? "half" : ""} ${invalid ? "invalid" : ""}`}
                            >
                                {field.kind !== "checkbox" && (
                                    <label htmlFor={id}>
                                        {field.label}
                                        {field.required && <span className="req" aria-hidden="true"> *</span>}
                                    </label>
                                )}

                                {field.kind === "textarea" ? (
                                    <textarea
                                        id={id}
                                        value={String(value ?? "")}
                                        placeholder={field.placeholder}
                                        aria-invalid={invalid}
                                        onChange={(e) => set(field.name, e.target.value)}
                                    />
                                ) : field.kind === "select" ? (
                                    <select
                                        id={id}
                                        value={String(value ?? "")}
                                        aria-invalid={invalid}
                                        onChange={(e) => {
                                            if (e.target.value === "__add_new__") {
                                                setAddingFor(field);
                                                return;
                                            }
                                            set(field.name, e.target.value);
                                        }}
                                    >
                                        {!field.required && <option value="">—</option>}
                                        {optionsFor(field).map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                        {(field.addNew || field.optionsFrom) && (
                                            <option value="__add_new__">
                                                ＋ {field.addNew?.title ?? field.optionsFrom?.addLabel ?? `Add ${field.label}`}…
                                            </option>
                                        )}
                                    </select>
                                ) : field.kind === "searchselect" ? (
                                    <SearchableSelect
                                        id={id}
                                        value={String(value ?? "")}
                                        options={optionsFor(field)}
                                        required={field.required}
                                        invalid={invalid}
                                        placeholder={field.placeholder}
                                        onChange={(v) => set(field.name, v)}
                                    />
                                ) : field.kind === "multiselect" ? (
                                    <div className="chip-picker">
                                        {(() => {
                                            // Values present but not in the list (e.g. a website
                                            // submission using an older service name) stay visible
                                            // as chips — nothing silently drops on save.
                                            const known = optionsFor(field);
                                            const current = (value as string[]) ?? [];
                                            const extras = current
                                                .filter((v) => !known.some((o) => o.value === v))
                                                .map((v) => ({ value: v, label: v }));
                                            return [...known, ...extras];
                                        })().map((o) => {
                                            const list = (value as string[]) ?? [];
                                            const on = list.includes(o.value);
                                            return (
                                                <button
                                                    type="button"
                                                    key={o.value}
                                                    className={`chip-toggle ${on ? "on" : ""}`}
                                                    aria-pressed={on}
                                                    onClick={() =>
                                                        set(
                                                            field.name,
                                                            on ? list.filter((x) => x !== o.value) : [...list, o.value]
                                                        )
                                                    }
                                                >
                                                    {o.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : field.kind === "checkbox" ? (
                                    <label className="check">
                                        <input
                                            id={id}
                                            type="checkbox"
                                            checked={Boolean(value)}
                                            onChange={(e) => set(field.name, e.target.checked)}
                                        />
                                        <span>{field.label}</span>
                                    </label>
                                ) : (
                                    <>
                                        <input
                                            id={id}
                                            type={
                                                field.kind === "datetime" ? "datetime-local"
                                                : field.kind === "number" ? "number"
                                                : field.kind
                                            }
                                            value={String(value ?? "")}
                                            placeholder={field.placeholder}
                                            min={field.min}
                                            max={field.max}
                                            list={field.suggestions ? `${id}-suggestions` : undefined}
                                            aria-invalid={invalid}
                                            onChange={(e) => set(field.name, e.target.value)}
                                        />
                                        {field.suggestions && (
                                            <datalist id={`${id}-suggestions`}>
                                                {field.suggestions.map((s) => <option key={s} value={s} />)}
                                            </datalist>
                                        )}
                                    </>
                                )}

                                {field.optionsFrom && optionsFor(field).length === 0 && (
                                    <span className="help">
                                        No {field.label.toLowerCase()} yet — choose “＋ {field.optionsFrom.addLabel ?? `Add ${field.label}`}…” to create the first one.
                                    </span>
                                )}
                                {field.help && <span className="help">{field.help}</span>}
                                {field.helpLink && (
                                    <a className="help" href={field.helpLink.href} target="_blank" rel="noreferrer">
                                        {field.helpLink.label} ↗
                                    </a>
                                )}
                            </div>
                        );
                    })}
                </div>

                {preview && <div style={{ marginTop: "0.9rem" }}>{preview(values)}</div>}

                {/* Lets Enter submit without a visible duplicate button. */}
                <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
            </form>

            {addingFor && (addingFor.addNew || addingFor.optionsFrom) && (
                <EntityForm
                    title={addingFor.addNew?.title ?? addingFor.optionsFrom?.addLabel ?? `Add ${addingFor.label}`}
                    subtitle={addingFor.addNew?.subtitle}
                    fields={
                        addingFor.addNew
                            ? addingFor.addNew.fields.filter((f) => {
                                  const from = addingFor.addNew!.inherit?.[f.name];
                                  return !(from && values[from]);
                              })
                            : ([{ name: "name", label: addingFor.label, kind: "text", required: true }] as Field[])
                    }
                    path={addingFor.addNew?.path ?? addingFor.optionsFrom!.path}
                    initial={addingFor.addNew?.initial}
                    fixed={
                        addingFor.addNew
                            ? Object.fromEntries(
                                  Object.entries(addingFor.addNew.inherit ?? {})
                                      .filter(([, from]) => Boolean(values[from]))
                                      .map(([child, from]) => [child, values[from]])
                              )
                            : undefined
                    }
                    onClose={() => setAddingFor(null)}
                    onSaved={(created) => {
                        const createdRow = (created ?? {}) as Record<string, unknown>;
                        if (addingFor.optionsFrom) {
                            // Vocabulary list: the parent record stores the NAME
                            // (text), not an id — so append and select by name.
                            const name = String(createdRow.name ?? "");
                            if (name) {
                                setExtraOptions((x) => ({
                                    ...x,
                                    [addingFor.name]: [...(x[addingFor.name] ?? []), { value: name, label: name }],
                                }));
                                setValues((v) => ({ ...v, [addingFor.name]: name }));
                            }
                        } else {
                            const id = typeof createdRow.id === "string" ? createdRow.id : "";
                            if (id) {
                                const label = addingFor.addNew!.optionLabel?.(createdRow)
                                    ?? String(createdRow.name
                                        ?? createdRow.full_name
                                        ?? [createdRow.first_name, createdRow.last_name].filter(Boolean).join(" ")
                                        ?? id);
                                setExtraOptions((x) => ({
                                    ...x,
                                    [addingFor.name]: [...(x[addingFor.name] ?? []), { value: id, label }],
                                }));
                                setValues((v) => ({ ...v, [addingFor.name]: id }));
                            }
                        }
                        setAddingFor(null);
                    }}
                />
            )}
        </Modal>
    );
};
